import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { readLastEmail } from "../auth/storage";
import OtpInput from "../components/OtpInput";
import { useCountdown } from "../hooks/useCountdown";
import AuthCard, { AUTH_INPUT } from "../components/common/AuthCard";
import { Button } from "@/components/ui/button";

type Stage = "email" | "otp";

const OTP_TTL_MS = 5 * 60 * 1_000;
const RESEND_DELAY_MS = 30 * 1_000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [envMode, setEnvMode] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState(() => readLastEmail() ?? "");

  useEffect(() => {
    let cancelled = false;
    api
      .modeStatus()
      .then((res) => {
        if (!cancelled) setEnvMode(res.environmentType ?? null);
      })
      .catch(() => {
        // Login page works without mode info — silently ignore.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpDeadline, setOtpDeadline] = useState<number | null>(null);
  const [resendDeadline, setResendDeadline] = useState<number | null>(null);

  const otpSecondsLeft = useCountdown(otpDeadline);
  const resendSecondsLeft = useCountdown(resendDeadline);

  const requestCode = async () => {
    setPending(true);
    setError(null);
    try {
      await api.requestCode(email);
      setStage("otp");
      setOtpDeadline(Date.now() + OTP_TTL_MS);
      setResendDeadline(Date.now() + RESEND_DELAY_MS);
    } catch (e) {
      setError(humanError(e, "Impossible d'envoyer le code. Réessaie."));
    } finally {
      setPending(false);
    }
  };

  const submitEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setError("Entre une adresse e-mail valide.");
      return;
    }
    void requestCode();
  };

  const verify = async (fullCode: string) => {
    setPending(true);
    setError(null);
    try {
      const { token } = await api.verifyCode(email, fullCode);
      login(token, email);
      const dest = (location.state as { from?: string } | null)?.from ?? "/dashboard";
      navigate(dest, { replace: true });
    } catch (e) {
      setCode("");
      setError(humanError(e, "Impossible de vérifier le code."));
    } finally {
      setPending(false);
    }
  };

  const resend = () => {
    if (resendSecondsLeft > 0) return;
    void requestCode();
  };

  return (
    <AuthCard title="Connexion" subtitle={envMode != null ? `DAX Bot — ${envMode}` : "DAX Bot"}>
      {stage === "email" && (
        <form onSubmit={submitEmail} className="flex flex-col gap-3">
          <label htmlFor="email" className="text-muted-foreground text-sm">
            E-mail
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            disabled={pending}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={AUTH_INPUT}
          />
          {error && (
            <p className="text-loss text-sm" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" disabled={pending || !email} className="w-full">
            {pending ? "Envoi…" : "Recevoir le code de vérification"}
          </Button>
        </form>
      )}

      {stage === "otp" && (
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">
            Un code à 6 chiffres a été envoyé à <strong className="text-foreground">{email}</strong>.
          </p>
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={(full) => void verify(full)}
            disabled={pending}
            autoFocus
          />
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {otpSecondsLeft > 0
                ? `Le code expire dans ${formatMmSs(otpSecondsLeft)}`
                : "Code expiré — demandes-en un nouveau"}
            </span>
            <button
              type="button"
              className="text-info disabled:text-muted-foreground hover:underline disabled:no-underline"
              onClick={resend}
              disabled={pending || resendSecondsLeft > 0}
            >
              {resendSecondsLeft > 0
                ? `Renvoyer (${resendSecondsLeft}s)`
                : "Renvoyer le code"}
            </button>
          </div>
          {error && (
            <p className="text-loss text-sm" role="alert">
              {error}
            </p>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setStage("email");
              setCode("");
              setError(null);
            }}
          >
            Changer d'e-mail
          </Button>
        </div>
      )}
    </AuthCard>
  );
}

function humanError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Code invalide ou expiré.";
    if (err.status === 429) return "Trop de tentatives. Réessaie plus tard.";
  }
  return fallback;
}

function formatMmSs(secs: number): string {
  const m = Math.floor(secs / 60).toString();
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}
